// Path: src/pages/UsersPage.tsx
// Author: ISTP_Dev
// Description: User management with School Filtering capability.

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowUpDown, ArrowUp, ArrowDown, Camera, Edit, Trash2, ToggleLeft, ToggleRight, Scan, Building2, RotateCcw, Search, Users } from 'lucide-react';
import AdminFaceEnrollModal from '../components/admin/AdminFaceEnrollModal';
import type { OperationMode } from '../components/admin/AdminFaceEnrollModal';

import {
  type School,
  type SchoolTime,
  type User,
  getSchools,
  createSchool,
  updateSchool,
  deleteSchool,
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  getRoles,
  getMemos,
  getSchoolTimes,
  createSchoolTime,
  updateSchoolTime,
  deleteSchoolTime,
  getDeletedSchools,
  getDeletedUsers,
  searchDeletedUsers,
  restoreSchool,
  restoreUser,
  transferUser,
  type SchoolWithDeletedUsers,
  getCities,
  getDistricts,
  getCourseTypes,
  createSchoolWithConfig,
  createCity,
  updateCity,
  deleteCity,
  createDistrict,
  updateDistrict,
  deleteDistrict,
  batchImportUsers,
  type City,
  type District,
  type CourseType,
  type SchoolCreateWithConfigResult,
  type BatchImportResult,
} from '../lib/api';
import { parseApiError } from '../lib/api/core';
import {
  adminToggleFaceLogin,
  adminTogglePasswordLogin,
  parseFaceError
} from '../lib/api/faceV2';

type UserFaceStatus = {
  [userId: number]: {
    has_face: boolean;
    face_login_enabled: boolean;
    password_login_enabled: boolean;
  };
};

const UsersPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'users' | 'schools' | 'times' | 'deleted' | 'search' | 'region'>('users');
  
  const [schools, setSchools] = useState<School[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedSchoolId, setSelectedSchoolId] = useState<number | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [selectedMemo, setSelectedMemo] = useState<string>('');

  const [roles, setRoles] = useState<string[]>([]);
  const [memos, setMemos] = useState<string[]>([]);

  type SortField = 'id' | 'username' | 'role' | 'school' | 'memo';
  type SortOrder = 'asc' | 'desc' | null;
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>(null);

  const [isSchoolModalOpen, setIsSchoolModalOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isFaceEnrollModalOpen, setIsFaceEnrollModalOpen] = useState(false);
  
  const [editingSchool, setEditingSchool] = useState<School | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [faceEnrollUser, setFaceEnrollUser] = useState<User | null>(null);
  const [faceEnrollMode, setFaceEnrollMode] = useState<OperationMode>('enroll');
  const [userFaceStatus, setUserFaceStatus] = useState<UserFaceStatus>({});
  const [toggleLoading, setToggleLoading] = useState<number | null>(null);
  
  const [schoolForm, setSchoolForm] = useState({ schoolname: '' });
  const [userForm, setUserForm] = useState({ 
    username: '', 
    password: '', 
    role: 'student' as 'sys' | 'admin' | 'teacher' | 'student', 
    school_id: '',
    memo: ''
  });

  // 班次管理状态
  const WEEKDAY_LABELS = ['', '周一', '周二', '周三', '周四', '周五', '周六', '周日'];
  const [schoolTimes, setSchoolTimes] = useState<SchoolTime[]>([]);
  const [timesLoading, setTimesLoading] = useState(false);
  const [timesSchoolId, setTimesSchoolId] = useState<number | null>(null);
  const [isTimeModalOpen, setIsTimeModalOpen] = useState(false);
  const [editingTime, setEditingTime] = useState<SchoolTime | null>(null);
  const [timeForm, setTimeForm] = useState({
    day_of_week: 1,
    start_time: '09:00:00',
    end_time: '10:30:00'
  });

  // 变更机构弹窗状态（需求 1.1）
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [transferTargetUser, setTransferTargetUser] = useState<User | null>(null);
  const [transferForm, setTransferForm] = useState<{ school_id: string }>({ school_id: '' });

  // 已删除数据选项卡状态（需求 1.2）
  const [deletedSchools, setDeletedSchools] = useState<SchoolWithDeletedUsers[]>([]);
  const [deletedUsersBySchool, setDeletedUsersBySchool] = useState<User[]>([]);
  const [selectedDeletedSchoolId, setSelectedDeletedSchoolId] = useState<number | null>(null);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [isRestoreUserModalOpen, setIsRestoreUserModalOpen] = useState(false);
  const [restoringUser, setRestoringUser] = useState<User | null>(null);
  const [restoreUserForm, setRestoreUserForm] = useState<{ new_school_id: string }>({ new_school_id: '' });
  const [restoreUserNeedsNewSchool, setRestoreUserNeedsNewSchool] = useState(false);

  // 用户名检索选项卡状态（需求 1.3）
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchActiveUsers, setSearchActiveUsers] = useState<User[]>([]);
  const [searchDeletedUsersList, setSearchDeletedUsersList] = useState<User[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // ============= 机构配置创建状态（第 15-16 章） =============
  const [isConfigSchoolModalOpen, setIsConfigSchoolModalOpen] = useState(false);
  const [cities, setCities] = useState<City[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [courseTypes, setCourseTypes] = useState<CourseType[]>([]);
  const [configSchoolForm, setConfigSchoolForm] = useState({
    city_id: '',
    district_id: '',
    is_trial: false,
    day_of_weeks: [] as number[],
    start_time: '09:00',
    end_time: '10:30',
    course_type_id: '',
  });
  const [configSchoolLoading, setConfigSchoolLoading] = useState(false);

  // ============= 批量导入用户状态（第 14 章） =============
  const [isBatchImportModalOpen, setIsBatchImportModalOpen] = useState(false);
  const [batchImportForm, setBatchImportForm] = useState({
    school_id: '',
    usernames_raw: '',
    role: 'student' as 'sys' | 'admin' | 'teacher' | 'student',
  });
  const [batchImportLoading, setBatchImportLoading] = useState(false);
  const [batchImportResult, setBatchImportResult] = useState<BatchImportResult | null>(null);

  // ============= 城市区域管理选项卡 state =============
  const [regionCities, setRegionCities] = useState<City[]>([]);
  const [regionDistricts, setRegionDistricts] = useState<District[]>([]);
  const [regionSelectedCityId, setRegionSelectedCityId] = useState<number | null>(null);
  const [regionLoading, setRegionLoading] = useState(false);

  const [isCityModalOpen, setIsCityModalOpen] = useState(false);
  const [editingCity, setEditingCity] = useState<City | null>(null);
  const [cityForm, setCityForm] = useState({ name: '' });

  const [isDistrictModalOpen, setIsDistrictModalOpen] = useState(false);
  const [editingDistrict, setEditingDistrict] = useState<District | null>(null);
  const [districtForm, setDistrictForm] = useState({ city_id: '', name: '' });

  const fetchSchools = async () => {
    try {
      const data = await getSchools({ limit: 100 });
      setSchools(data.data);
    } catch (error) {
      console.error("Failed to load schools:", error);
    }
  };

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const params: any = { limit: 100 };
      if (selectedSchoolId !== null) {
        params.school_id = selectedSchoolId;
      }
      
      const data = await getUsers(params);
      setUsers(data.data);
    } catch (error) {
      console.error("Failed to load users:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRolesAndMemos = async () => {
    try {
      const rolesRes = await getRoles();
      const rolesData = (rolesRes as any).data || rolesRes;
      setRoles(Array.isArray(rolesData) ? rolesData : []);

      const memosRes = await getMemos();
      const memosData = (memosRes as any).data || memosRes;
      setMemos(Array.isArray(memosData) ? memosData : []);
    } catch (error) {
      console.error("Failed to load roles/memos:", error);
    }
  };

  useEffect(() => {
    fetchSchools();
    fetchRolesAndMemos();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [selectedSchoolId]);

  // 切换到"已删除数据"选项卡时自动拉取已删除学校列表
  useEffect(() => {
    if (activeTab === 'deleted' && deletedSchools.length === 0) {
      fetchDeletedSchools();
    }
  }, [activeTab]);

  const fetchDeletedSchools = async () => {
    setDeletedLoading(true);
    try {
      const data = await getDeletedSchools({ limit: 100 });
      setDeletedSchools(data.data);
    } catch (error) {
      console.error('Failed to load deleted schools:', error);
    } finally {
      setDeletedLoading(false);
    }
  };

  const fetchDeletedUsersForSchool = async (schoolId: number) => {
    setDeletedLoading(true);
    try {
      const data = await getDeletedUsers({ school_id: schoolId, limit: 100 });
      setDeletedUsersBySchool(data.data);
    } catch (error) {
      console.error('Failed to load deleted users:', error);
    } finally {
      setDeletedLoading(false);
    }
  };

  // ============= 需求 1.1：变更机构 =============
  const openTransferSchool = (user: User) => {
    setTransferTargetUser(user);
    setTransferForm({ school_id: user.school_id ? user.school_id.toString() : '' });
    setIsTransferModalOpen(true);
  };

  const handleTransferSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!transferTargetUser) return;
    try {
      const newSchoolId = transferForm.school_id ? Number(transferForm.school_id) : null;
      await transferUser(transferTargetUser.id, { new_school_id: newSchoolId });
      setIsTransferModalOpen(false);
      fetchUsers(); // 立即重新获取用户列表（需求 1.1 明确要求）
    } catch (error: any) {
      const err = parseApiError(error);
      alert(err.message);
    }
  };

  // ============= 需求 1.2：恢复学校 =============
  const handleRestoreSchool = async (schoolId: number) => {
    if (!window.confirm('恢复该学校将连带恢复其下所有已删除用户（用户名冲突的会跳过）。确认继续？')) return;
    try {
      const res = await restoreSchool(schoolId);
      const result = res.data;
      let msg = `${result.message}\n成功恢复用户数：${result.restored_users_count}`;
      if (result.conflict_users?.length > 0) {
        msg += `\n\n以下用户因用户名冲突未恢复（需先改名或删除占用用户）：`;
        result.conflict_users.forEach(c => {
          msg += `\n- ${c.username}（ID: ${c.user_id}，被用户 ID: ${c.conflict_user_id} 占用）`;
        });
      }
      alert(msg);
      fetchDeletedSchools();
      setDeletedUsersBySchool([]);
      setSelectedDeletedSchoolId(null);
    } catch (error: any) {
      const err = parseApiError(error);
      alert(err.message);
    }
  };

  // ============= 需求 1.2：恢复单个用户 =============
  const openRestoreUser = (user: User, schoolIsDeleted: boolean) => {
    setRestoringUser(user);
    setRestoreUserNeedsNewSchool(schoolIsDeleted);
    setRestoreUserForm({ new_school_id: '' });
    setIsRestoreUserModalOpen(true);
  };

  const handleRestoreUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!restoringUser) return;
    try {
      const payload: { new_school_id?: number | null } = {};
      if (restoreUserForm.new_school_id) {
        payload.new_school_id = Number(restoreUserForm.new_school_id);
      } else if (restoreUserNeedsNewSchool) {
        alert('原机构已删除，必须指定新的机构');
        return;
      }
      await restoreUser(restoringUser.id, payload);
      setIsRestoreUserModalOpen(false);
      if (selectedDeletedSchoolId) fetchDeletedUsersForSchool(selectedDeletedSchoolId);
    } catch (error: any) {
      const err = parseApiError(error);
      if (err.code === 'SCHOOL_DELETED') {
        setRestoreUserNeedsNewSchool(true);
        alert(err.message);
        return;
      }
      alert(err.message);
    }
  };

  // ============= 需求 1.3：用户名检索 =============
  const handleUsernameSearch = async () => {
    const keyword = searchKeyword.trim();
    if (!keyword) {
      alert('请输入用户名关键字');
      return;
    }
    setSearchLoading(true);
    setHasSearched(true);
    try {
      // 并行：未删除用户（getUsers 不支持 username 参数，客户端过滤）+ 已删除用户（专用搜索接口）
      const [activeRes, deletedRes] = await Promise.all([
        getUsers({ limit: 100 }),
        searchDeletedUsers({ username: keyword }),
      ]);
      const activeList = (activeRes.data || []).filter(u =>
        u.username.toLowerCase().includes(keyword.toLowerCase())
      );
      setSearchActiveUsers(activeList);
      setSearchDeletedUsersList(deletedRes.data || []);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    let result = users.filter(user => {
      if (selectedRole && user.role !== selectedRole) return false;
      if (selectedMemo && user.memo !== selectedMemo) return false;
      return true;
    });

    if (sortField && sortOrder) {
      result = [...result].sort((a, b) => {
        let aVal: string | number = '';
        let bVal: string | number = '';

        switch (sortField) {
          case 'id':
            aVal = a.id;
            bVal = b.id;
            break;
          case 'username':
            aVal = a.username.toLowerCase();
            bVal = b.username.toLowerCase();
            break;
          case 'role':
            aVal = a.role;
            bVal = b.role;
            break;
          case 'school':
            aVal = a.school?.schoolname?.toLowerCase() || '';
            bVal = b.school?.schoolname?.toLowerCase() || '';
            break;
          case 'memo':
            aVal = a.memo?.toLowerCase() || '';
            bVal = b.memo?.toLowerCase() || '';
            break;
        }

        if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [users, selectedRole, selectedMemo, sortField, sortOrder]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortOrder === 'asc') {
        setSortOrder('desc');
      } else if (sortOrder === 'desc') {
        setSortField(null);
        setSortOrder(null);
      }
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const SortHeader = ({ field, label }: { field: SortField; label: string }) => {
    const isActive = sortField === field;
    return (
      <th 
        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none"
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-1">
          <span>{label}</span>
          {isActive ? (
            sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
          ) : (
            <ArrowUpDown size={14} className="text-gray-300" />
          )}
        </div>
      </th>
    );
  };

  // ============= 机构配置创建（第 15-16 章） =============
  const fetchLookupData = async () => {
    try {
      const [citiesRes, courseTypesRes] = await Promise.all([
        getCities(),
        getCourseTypes(),
      ]);
      setCities((citiesRes as any).data || citiesRes || []);
      setCourseTypes((courseTypesRes as any).data || courseTypesRes || []);
    } catch (error) {
      console.error('Failed to load lookup data:', error);
    }
  };

  const handleConfigCityChange = async (cityId: string) => {
    setConfigSchoolForm(prev => ({
      ...prev,
      city_id: cityId,
      district_id: '',
    }));
    if (cityId) {
      try {
        const res = await getDistricts({ city_id: Number(cityId) });
        setDistricts((res as any).data || res || []);
      } catch (error) {
        console.error('Failed to load districts:', error);
        setDistricts([]);
      }
    } else {
      setDistricts([]);
    }
  };

  const handleDayOfWeekToggle = (day: number) => {
    setConfigSchoolForm(prev => {
      const exists = prev.day_of_weeks.includes(day);
      return {
        ...prev,
        day_of_weeks: exists
          ? prev.day_of_weeks.filter(d => d !== day)
          : [...prev.day_of_weeks, day].sort(),
      };
    });
  };

  // 客户端预览学校名称（按文档 16.4 三种模式拼接）
  const previewSchoolname = (): string => {
    const city = cities.find(c => c.id === Number(configSchoolForm.city_id));
    const district = districts.find(d => d.id === Number(configSchoolForm.district_id));
    if (!city || !district) return '';
    const base = `${city.name}_${district.name}`;
    const hasSchedule = configSchoolForm.day_of_weeks.length > 0
      && configSchoolForm.start_time
      && configSchoolForm.end_time
      && configSchoolForm.course_type_id;
    if (!hasSchedule) return base; // 模式 A
    const dayMap: Record<number, string> = { 1: '一', 2: '二', 3: '三', 4: '四', 5: '五', 6: '六', 7: '日' };
    const daysStr = configSchoolForm.day_of_weeks.map(d => dayMap[d]).join('');
    const startStr = configSchoolForm.start_time.replace(':', '');
    const endStr = configSchoolForm.end_time.replace(':', '');
    const courseType = courseTypes.find(ct => ct.id === Number(configSchoolForm.course_type_id));
    const code = courseType?.code || '';
    if (configSchoolForm.is_trial) {
      return `${base}_体验_周${daysStr}_${startStr}_${endStr}_${code}`; // 模式 C
    }
    return `${base}_周${daysStr}_${startStr}_${endStr}_${code}`; // 模式 B
  };

  const openCreateSchoolWithConfig = () => {
    if (cities.length === 0) fetchLookupData();
    setConfigSchoolForm({
      city_id: '', district_id: '', is_trial: false,
      day_of_weeks: [], start_time: '09:00', end_time: '10:30', course_type_id: '',
    });
    setDistricts([]);
    setIsConfigSchoolModalOpen(true);
  };

  const handleConfigSchoolSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // 客户端条件必填校验（文档 16.3）
    const needsSchedule = configSchoolForm.is_trial || configSchoolForm.day_of_weeks.length > 0;
    if (needsSchedule) {
      const missing: string[] = [];
      if (configSchoolForm.day_of_weeks.length === 0) missing.push('上课日');
      if (!configSchoolForm.start_time) missing.push('开始时间');
      if (!configSchoolForm.end_time) missing.push('结束时间');
      if (!configSchoolForm.course_type_id) missing.push('课程类型');
      if (missing.length > 0) {
        alert(`选择了体验课或上课日后，必须补全：${missing.join('、')}`);
        return;
      }
    }
    if (configSchoolForm.start_time && configSchoolForm.end_time
        && configSchoolForm.start_time >= configSchoolForm.end_time) {
      alert('开始时间必须早于结束时间');
      return;
    }
    setConfigSchoolLoading(true);
    try {
      const payload: any = {
        city_id: Number(configSchoolForm.city_id),
        district_id: Number(configSchoolForm.district_id),
      };
      if (configSchoolForm.is_trial) payload.is_trial = true;
      if (configSchoolForm.day_of_weeks.length > 0) {
        payload.day_of_weeks = configSchoolForm.day_of_weeks;
        payload.start_time = configSchoolForm.start_time + ':00';
        payload.end_time = configSchoolForm.end_time + ':00';
        payload.course_type_id = Number(configSchoolForm.course_type_id);
      }
      await createSchoolWithConfig(payload);
      setIsConfigSchoolModalOpen(false);
      fetchSchools();
    } catch (error: any) {
      const err = parseApiError(error);
      alert(err.message);
    } finally {
      setConfigSchoolLoading(false);
    }
  };

  // ============= 批量导入用户（第 14 章） =============
  const openBatchImport = () => {
    const defaultSchoolId = selectedSchoolId ? selectedSchoolId.toString() : '';
    setBatchImportForm({
      school_id: defaultSchoolId,
      usernames_raw: '',
      role: 'student',
    });
    setBatchImportResult(null);
    setIsBatchImportModalOpen(true);
  };

  const handleBatchImportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!batchImportForm.school_id) {
      alert('请选择目标机构');
      return;
    }
    if (!batchImportForm.usernames_raw.trim()) {
      alert('请输入至少一个用户名');
      return;
    }
    setBatchImportLoading(true);
    try {
      const res = await batchImportUsers({
        school_id: Number(batchImportForm.school_id),
        usernames_raw: batchImportForm.usernames_raw,
        role: batchImportForm.role,
      });
      setBatchImportResult(res.data);
      fetchUsers(); // 刷新用户列表
    } catch (error: any) {
      const err = parseApiError(error);
      alert(err.message);
    } finally {
      setBatchImportLoading(false);
    }
  };

  const openCreateSchool = () => {
    setEditingSchool(null);
    setSchoolForm({ schoolname: '' });
    setIsSchoolModalOpen(true);
  };

  const openEditSchool = (school: School) => {
    setEditingSchool(school);
    setSchoolForm({ schoolname: school.schoolname });
    setIsSchoolModalOpen(true);
  };

  const handleSchoolSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingSchool) {
        await updateSchool(editingSchool.id, { schoolname: schoolForm.schoolname });
      } else {
        await createSchool({ schoolname: schoolForm.schoolname });
      }
      setIsSchoolModalOpen(false);
      fetchSchools();
    } catch (error: any) {
      console.error("School operation failed:", error);
      const err = parseApiError(error);
      alert(err.message);
    }
  };

  const handleDeleteSchool = async (id: number) => {
    if (!window.confirm("确定要删除该学校吗？该操作会连带删除该机构下所有未删除用户。")) return;
    try {
      const res = await deleteSchool(id);
      const result = res.data;
      alert(`${result.message}\n连带删除用户数：${result.deleted_users_count}`);
      if (selectedSchoolId === id) setSelectedSchoolId(null);
      fetchSchools();
    } catch (error: any) {
      console.error("Delete school failed:", error);
      const err = parseApiError(error);
      alert(err.message);
    }
  };

  // ============= 城市区域管理 =============
  const fetchRegionCities = async () => {
    setRegionLoading(true);
    try {
      const res = await getCities();
      setRegionCities((res as any).data || res || []);
    } catch (e) {
      console.error('Failed to load region cities:', e);
    } finally {
      setRegionLoading(false);
    }
  };

  const fetchRegionDistricts = async (cityId: number) => {
    try {
      const res = await getDistricts({ city_id: cityId });
      setRegionDistricts((res as any).data || res || []);
    } catch (e) {
      console.error('Failed to load region districts:', e);
      setRegionDistricts([]);
    }
  };

  // 切到 region tab 时首次加载城市
  useEffect(() => {
    if (activeTab === 'region' && regionCities.length === 0) fetchRegionCities();
  }, [activeTab]);

  // 选中城市变化时拉区县
  useEffect(() => {
    if (regionSelectedCityId !== null) fetchRegionDistricts(regionSelectedCityId);
    else setRegionDistricts([]);
  }, [regionSelectedCityId]);

  // 城市弹窗
  const openCreateCity = () => {
    setEditingCity(null);
    setCityForm({ name: '' });
    setIsCityModalOpen(true);
  };
  const openEditCity = (city: City) => {
    setEditingCity(city);
    setCityForm({ name: city.name });
    setIsCityModalOpen(true);
  };
  const handleCitySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingCity) {
        await updateCity(editingCity.id, { name: cityForm.name });
      } else {
        await createCity({ name: cityForm.name });
      }
      setIsCityModalOpen(false);
      fetchRegionCities();
    } catch (error: any) {
      const err = parseApiError(error);
      alert(err.message);
    }
  };
  const handleDeleteCity = async (id: number) => {
    if (!window.confirm('删除城市将级联软删除其下所有区县、人脸记录并禁用相关用户刷脸登录。确认继续？')) return;
    try {
      const res = await deleteCity(id);
      const r = res.data;
      alert(`${r.message}\n级联删除区县数：${r.deleted_district_count}${r.deleted_face_count != null ? `\n禁用人脸记录数：${r.deleted_face_count}` : ''}`);
      if (regionSelectedCityId === id) setRegionSelectedCityId(null);
      fetchRegionCities();
    } catch (error: any) {
      const err = parseApiError(error);
      alert(err.message);
    }
  };

  // 区县弹窗
  const openCreateDistrict = () => {
    if (regionSelectedCityId === null) {
      alert('请先在城市列表中选择一个城市');
      return;
    }
    setEditingDistrict(null);
    setDistrictForm({ city_id: regionSelectedCityId.toString(), name: '' });
    setIsDistrictModalOpen(true);
  };
  const openEditDistrict = (district: District) => {
    setEditingDistrict(district);
    setDistrictForm({ city_id: district.city_id.toString(), name: district.name });
    setIsDistrictModalOpen(true);
  };
  const handleDistrictSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingDistrict) {
        const payload: { city_id?: number; name?: string } = {};
        if (Number(districtForm.city_id) !== editingDistrict.city_id) payload.city_id = Number(districtForm.city_id);
        if (districtForm.name !== editingDistrict.name) payload.name = districtForm.name;
        await updateDistrict(editingDistrict.id, payload);
      } else {
        await createDistrict({ city_id: Number(districtForm.city_id), name: districtForm.name });
      }
      setIsDistrictModalOpen(false);
      if (regionSelectedCityId !== null) fetchRegionDistricts(regionSelectedCityId);
    } catch (error: any) {
      const err = parseApiError(error);
      alert(err.message);
    }
  };
  const handleDeleteDistrict = async (id: number) => {
    if (!window.confirm('确定软删除该区县？')) return;
    try {
      await deleteDistrict(id);
      if (regionSelectedCityId !== null) fetchRegionDistricts(regionSelectedCityId);
    } catch (error: any) {
      const err = parseApiError(error);
      alert(err.message);
    }
  };

  const openCreateUser = () => {
    setEditingUser(null);
    const defaultSchoolId = selectedSchoolId ? selectedSchoolId.toString() : '';
    
    setUserForm({ 
      username: '', 
      password: '', 
      role: 'student', 
      school_id: defaultSchoolId,
      memo: ''
    });
    setIsUserModalOpen(true);
  };

  const openEditUser = (user: User) => {
    setEditingUser(user);
    setUserForm({ 
      username: user.username, 
      password: '', 
      role: user.role, 
      school_id: user.school_id ? user.school_id.toString() : '',
      memo: user.memo || ''
    });
    setIsUserModalOpen(true);
  };

  const openFaceEnroll = (user: User, mode: OperationMode) => {
    setFaceEnrollUser(user);
    setFaceEnrollMode(mode);
    setIsFaceEnrollModalOpen(true);
  };

  const handleToggleFaceLogin = async (user: User, enabled: boolean) => {
    if (!user.face_login_enabled && !enabled) return;
    if (!user.has_face_enrolled && enabled) {
      alert('用户未录入人脸，无法启用刷脸登录');
      return;
    }
    
    setToggleLoading(user.id);
    try {
      const response = await adminToggleFaceLogin(user.id, enabled);
      if (response.data.success) {
        setUserFaceStatus(prev => ({
          ...prev,
          [user.id]: {
            ...prev[user.id],
            face_login_enabled: enabled
          }
        }));
        fetchUsers();
      }
    } catch (err: any) {
      const errorMsg = parseFaceError(err);
      alert(errorMsg);
    } finally {
      setToggleLoading(null);
    }
  };

  const handleTogglePasswordLogin = async (user: User, enabled: boolean) => {
    if (!user.password_login_enabled && !enabled) {
      if (!user.face_login_enabled) {
        alert('用户未启用刷脸登录，无法禁用密码登录');
        return;
      }
    }
    
    setToggleLoading(user.id);
    try {
      const response = await adminTogglePasswordLogin(user.id, enabled);
      if (response.data.success) {
        setUserFaceStatus(prev => ({
          ...prev,
          [user.id]: {
            ...prev[user.id],
            password_login_enabled: enabled
          }
        }));
        fetchUsers();
      }
    } catch (err: any) {
      const errorMsg = parseFaceError(err);
      alert(errorMsg);
    } finally {
      setToggleLoading(null);
    }
  };

  const handleUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload: any = {
        username: userForm.username,
        role: userForm.role,
        school_id: userForm.school_id ? Number(userForm.school_id) : undefined,
        memo: userForm.memo || undefined
      };
      
      if (!editingUser || userForm.password) {
        payload.password = userForm.password;
      }

      if (editingUser) {
        await updateUser(editingUser.id, payload);
      } else {
        await createUser(payload);
      }
      setIsUserModalOpen(false);
      fetchUsers();
    } catch (error: any) {
      console.error("User operation failed:", error);
      const err = parseApiError(error);
      alert(err.message);
    }
  };

  const handleDeleteUser = async (id: number) => {
    if (!window.confirm("确定要删除该用户吗？")) return;
    try {
      await deleteUser(id);
      fetchUsers();
    } catch (error: any) {
      console.error("Delete user failed:", error);
      const err = parseApiError(error);
      alert(err.message);
    }
  };

  // ============= 班次管理 CRUD =============
  const fetchSchoolTimes = async (schoolId: number) => {
    setTimesLoading(true);
    try {
      const data: any = await getSchoolTimes(schoolId);
      const list = Array.isArray(data) ? data : (data?.data ?? []);
      setSchoolTimes(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Failed to load school times:", error);
      setSchoolTimes([]);
    } finally {
      setTimesLoading(false);
    }
  };

  const openCreateTime = () => {
    setEditingTime(null);
    setTimeForm({ day_of_week: 1, start_time: '09:00:00', end_time: '10:30:00' });
    setIsTimeModalOpen(true);
  };

  const openEditTime = (time: SchoolTime) => {
    setEditingTime(time);
    setTimeForm({
      day_of_week: time.day_of_week,
      start_time: time.start_time,
      end_time: time.end_time
    });
    setIsTimeModalOpen(true);
  };

  const handleTimeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!timesSchoolId) return;
    // 前端校验
    if (timeForm.day_of_week < 1 || timeForm.day_of_week > 7) {
      alert('day_of_week 必须在 1-7 之间');
      return;
    }
    if (timeForm.start_time >= timeForm.end_time) {
      alert('start_time 必须早于 end_time');
      return;
    }
    try {
      if (editingTime) {
        await updateSchoolTime(timesSchoolId, editingTime.id, {
          day_of_week: timeForm.day_of_week,
          start_time: timeForm.start_time,
          end_time: timeForm.end_time
        });
      } else {
        await createSchoolTime(timesSchoolId, {
          day_of_week: timeForm.day_of_week,
          start_time: timeForm.start_time,
          end_time: timeForm.end_time
        });
      }
      setIsTimeModalOpen(false);
      fetchSchoolTimes(timesSchoolId);
    } catch (error: any) {
      console.error("Time operation failed:", error);
      const detail = error?.response?.data?.detail || '操作失败';
      alert(detail);
    }
  };

  const handleDeleteTime = async (timeId: number) => {
    if (!timesSchoolId) return;
    if (!window.confirm("确定要删除该班次吗？")) return;
    try {
      await deleteSchoolTime(timesSchoolId, timeId);
      fetchSchoolTimes(timesSchoolId);
    } catch (error: any) {
      console.error("Delete time failed:", error);
      const detail = error?.response?.data?.detail || '删除失败';
      alert(detail);
    }
  };

  const RoleBadge = ({ role }: { role: string }) => {
    const colors: Record<string, string> = {
      sys: 'bg-purple-100 text-purple-800',
      admin: 'bg-blue-100 text-blue-800',
      teacher: 'bg-green-100 text-green-800',
      student: 'bg-gray-100 text-gray-800'
    };
    return (
      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${colors[role] || colors.student}`}>
        {role.toUpperCase()}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8 font-sans">
      <div className="max-w-6xl mx-auto">
        <header className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <Link 
              to="/admin" 
              className="flex items-center gap-1 text-gray-500 hover:text-gray-700 transition-colors"
              title="返回管理控制台"
            >
              <ArrowLeft size={16} />
              <span className="text-sm">返回</span>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">系统管理中心</h1>
              <p className="text-gray-500 mt-1">管理平台所有的注册机构与用户信息</p>
            </div>
          </div>
          <button 
            onClick={() => { fetchSchools(); fetchUsers(); }}
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
          >
            {isLoading ? '加载中...' : '🔄 刷新数据'}
          </button>
        </header>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('users')}
              className={`${activeTab === 'users' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
            >
              用户管理
            </button>
            <button
              onClick={() => setActiveTab('schools')}
              className={`${activeTab === 'schools' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
            >
              机构管理
            </button>
            <button
              onClick={() => setActiveTab('times')}
              className={`${activeTab === 'times' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
            >
              班次管理
            </button>
            <button
              onClick={() => setActiveTab('deleted')}
              className={`${activeTab === 'deleted' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
            >
              已删除数据
            </button>
            <button
              onClick={() => setActiveTab('search')}
              className={`${activeTab === 'search' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
            >
              用户名检索
            </button>
            <button
              onClick={() => setActiveTab('region')}
              className={`${activeTab === 'region' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
            >
              城市区域管理
            </button>
          </nav>
        </div>

        {/* --- USERS TAB --- */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex flex-col gap-4">
              
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <div className="flex items-center gap-4">
                  <h2 className="text-lg font-medium text-gray-900 whitespace-nowrap">用户列表 ({filteredUsers.length})</h2>
                  
                  <div className="relative">
                    <select
                      value={selectedSchoolId === null ? '' : selectedSchoolId}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSelectedSchoolId(val === '' ? null : Number(val));
                      }}
                      className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                    >
                      <option value="">🏠 所有机构</option>
                      {schools
                        .filter(s => !s.is_deleted)
                        .map((school) => (
                          <option key={school.id} value={school.id}>
                            {school.schoolname}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>

                <div className="flex gap-2 w-full sm:w-auto">
                  <button 
                    onClick={openCreateUser}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex-1 sm:flex-none"
                  >
                    + 新增用户
                  </button>
                  <button 
                    onClick={openBatchImport}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 flex-1 sm:flex-none"
                  >
                    <Users size={16} />
                    批量导入
                  </button>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600 whitespace-nowrap">角色筛选:</label>
                  <select
                    value={selectedRole}
                    onChange={e => setSelectedRole(e.target.value)}
                    className="px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none min-w-[120px]"
                  >
                    <option value="">全部角色</option>
                    {roles.map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600 whitespace-nowrap">备注筛选:</label>
                  <select
                    value={selectedMemo}
                    onChange={e => setSelectedMemo(e.target.value)}
                    className="px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none min-w-[150px]"
                  >
                    <option value="">全部备注</option>
                    {memos.map(memo => (
                      <option key={memo} value={memo}>{memo}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortHeader field="id" label="ID" />
                    <SortHeader field="username" label="用户名" />
                    <SortHeader field="role" label="角色" />
                    <SortHeader field="school" label="所属机构" />
                    <SortHeader field="memo" label="备注" />
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">人脸状态</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">登录方式</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">#{user.id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.username}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <RoleBadge role={user.role} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.school?.schoolname || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={user.memo || ''}>
                        {user.memo || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {user.has_face_enrolled ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <Scan size={12} />
                            已录入
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            未录入
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-3">
                          <button
                            onClick={() => handleToggleFaceLogin(user, !user.face_login_enabled)}
                            disabled={toggleLoading === user.id || (!user.has_face_enrolled && !user.face_login_enabled)}
                            className={`inline-flex items-center gap-1 text-xs transition-colors ${
                              user.face_login_enabled 
                                ? 'text-green-600 hover:text-green-800' 
                                : 'text-gray-400 hover:text-gray-600'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                            title={user.has_face_enrolled ? (user.face_login_enabled ? '点击禁用刷脸登录' : '点击启用刷脸登录') : '需先录入人脸'}
                          >
                            {user.face_login_enabled ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                            刷脸
                          </button>
                          <button
                            onClick={() => handleTogglePasswordLogin(user, !user.password_login_enabled)}
                            disabled={toggleLoading === user.id}
                            className={`inline-flex items-center gap-1 text-xs transition-colors ${
                              user.password_login_enabled 
                                ? 'text-blue-600 hover:text-blue-800' 
                                : 'text-gray-400 hover:text-gray-600'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                            title={user.password_login_enabled ? '点击禁用密码登录' : '点击启用密码登录'}
                          >
                            {user.password_login_enabled ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                            密码
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button onClick={() => openEditUser(user)} className="text-indigo-600 hover:text-indigo-900 mr-3">编辑</button>
                        <button
                          onClick={() => openTransferSchool(user)}
                          className="text-cyan-600 hover:text-cyan-900 mr-3 inline-flex items-center gap-1"
                          title="变更所属机构"
                        >
                          <Building2 size={14} />
                          变更机构
                        </button>
                        <button
                          onClick={() => openFaceEnroll(user, 'enroll')}
                          className="text-green-600 hover:text-green-900 mr-2 inline-flex items-center gap-1"
                          title="录入人脸"
                        >
                          <Camera size={14} />
                          录入
                        </button>
                        <button 
                          onClick={() => openFaceEnroll(user, 'update')} 
                          className="text-blue-600 hover:text-blue-900 mr-2 inline-flex items-center gap-1"
                          title="修改人脸"
                        >
                          <Edit size={14} />
                          修改
                        </button>
                        <button 
                          onClick={() => openFaceEnroll(user, 'delete')} 
                          className="text-orange-600 hover:text-orange-900 mr-3 inline-flex items-center gap-1"
                          title="删除人脸"
                        >
                          <Trash2 size={14} />
                          删除
                        </button>
                        <button onClick={() => handleDeleteUser(user.id)} className="text-red-600 hover:text-red-900">删除用户</button>
                      </td>
                    </tr>
                  ))}
                  {filteredUsers.length === 0 && !isLoading && (
                    <tr><td colSpan={8} className="text-center py-8 text-gray-400">
                      {selectedSchoolId || selectedRole || selectedMemo ? '当前筛选条件下无用户' : '暂无数据'}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* --- SCHOOLS TAB --- */}
        {activeTab === 'schools' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-lg font-medium text-gray-900">机构列表 ({schools.length})</h2>
              <button 
                onClick={openCreateSchoolWithConfig}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                + 配置创建机构
              </button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">学校名称</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {schools.map((school) => (
                    <tr key={school.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">#{school.id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{school.schoolname}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {school.is_deleted ? 
                          <span className="text-red-500 flex items-center gap-1">🚫 已停用</span> : 
                          <span className="text-green-500 flex items-center gap-1">✅ 正常</span>
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button onClick={() => openEditSchool(school)} className="text-indigo-600 hover:text-indigo-900 mr-4">编辑</button>
                        <button onClick={() => handleDeleteSchool(school.id)} className="text-red-600 hover:text-red-900">删除</button>
                      </td>
                    </tr>
                  ))}
                  {schools.length === 0 && !isLoading && (
                    <tr><td colSpan={4} className="text-center py-8 text-gray-400">暂无数据</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* --- TIMES TAB (班次管理) --- */}
        {activeTab === 'times' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div className="flex items-center gap-4">
                <h2 className="text-lg font-medium text-gray-900 whitespace-nowrap">
                  班次列表 ({schoolTimes.length})
                </h2>
                <select
                  value={timesSchoolId === null ? '' : timesSchoolId}
                  onChange={(e) => {
                    const val = e.target.value;
                    const id = val === '' ? null : Number(val);
                    setTimesSchoolId(id);
                    if (id !== null) fetchSchoolTimes(id);
                    else setSchoolTimes([]);
                  }}
                  className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                >
                  <option value="">🏠 请选择机构</option>
                  {schools
                    .filter(s => !s.is_deleted)
                    .map((school) => (
                      <option key={school.id} value={school.id}>
                        {school.schoolname}
                      </option>
                    ))}
                </select>
              </div>
              <button
                onClick={openCreateTime}
                disabled={timesSchoolId === null}
                className="bg-amber-600 hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors w-full sm:w-auto"
              >
                + 新增班次
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">星期几</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">开始时间</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">结束时间</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {schoolTimes.map((time) => (
                    <tr key={time.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">#{time.id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {WEEKDAY_LABELS[time.day_of_week] || `星期${time.day_of_week}`}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{time.start_time}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{time.end_time}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button onClick={() => openEditTime(time)} className="text-indigo-600 hover:text-indigo-900 mr-4">编辑</button>
                        <button onClick={() => handleDeleteTime(time.id)} className="text-red-600 hover:text-red-900">删除</button>
                      </td>
                    </tr>
                  ))}
                  {timesSchoolId === null && !timesLoading && (
                    <tr><td colSpan={5} className="text-center py-8 text-gray-400">请先选择机构</td></tr>
                  )}
                  {timesSchoolId !== null && schoolTimes.length === 0 && !timesLoading && (
                    <tr><td colSpan={5} className="text-center py-8 text-gray-400">暂无班次</td></tr>
                  )}
                  {timesLoading && (
                    <tr><td colSpan={5} className="text-center py-8 text-gray-400">加载中...</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* --- DELETED TAB (已删除数据，需求 1.2) --- */}
        {activeTab === 'deleted' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-lg font-medium text-gray-900">
                已删除数据 ({deletedSchools.length} 所学校)
              </h2>
              <button
                onClick={() => { fetchDeletedSchools(); setDeletedUsersBySchool([]); setSelectedDeletedSchoolId(null); }}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                🔄 刷新
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 divide-x divide-gray-200">
              {/* 左侧：已删除学校列表 */}
              <div className="p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">已删除学校</h3>
                {deletedLoading && deletedSchools.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">加载中...</div>
                ) : deletedSchools.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">暂无已删除学校</div>
                ) : (
                  <ul className="space-y-2 max-h-[480px] overflow-y-auto">
                    {deletedSchools.map(school => (
                      <li
                        key={school.id}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedDeletedSchoolId === school.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:bg-gray-50'
                        }`}
                        onClick={() => {
                          setSelectedDeletedSchoolId(school.id);
                          fetchDeletedUsersForSchool(school.id);
                        }}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="font-medium text-gray-900">#{school.id} {school.schoolname}</div>
                            <div className="text-xs text-gray-500 mt-1">
                              已删除用户数：{school.deleted_users_count}
                            </div>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleRestoreSchool(school.id); }}
                            className="text-green-600 hover:text-green-800 text-sm inline-flex items-center gap-1"
                            title="恢复学校（连带恢复所有已删除用户）"
                          >
                            <RotateCcw size={14} />
                            恢复学校
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* 右侧：选中学校的已删除用户列表 */}
              <div className="p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  {selectedDeletedSchoolId ? '该学校下的已删除用户' : '请从左侧选择学校'}
                </h3>
                {selectedDeletedSchoolId === null ? (
                  <div className="text-center py-8 text-gray-400">未选择学校</div>
                ) : deletedLoading ? (
                  <div className="text-center py-8 text-gray-400">加载中...</div>
                ) : deletedUsersBySchool.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">该学校下无已删除用户</div>
                ) : (
                  <ul className="space-y-2 max-h-[480px] overflow-y-auto">
                    {deletedUsersBySchool.map(user => (
                      <li key={user.id} className="p-3 rounded-lg border border-gray-200 flex justify-between items-center">
                        <div>
                          <div className="font-medium text-gray-900">#{user.id} {user.username}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            角色：{user.role} | 所属机构：{user.school?.schoolname || '-'}
                          </div>
                        </div>
                        <button
                          onClick={() => openRestoreUser(user, true)}
                          className="text-green-600 hover:text-green-800 text-sm inline-flex items-center gap-1"
                          title="恢复用户"
                        >
                          <RotateCcw size={14} />
                          恢复用户
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {/* --- SEARCH TAB (用户名检索，需求 1.3) --- */}
        {activeTab === 'search' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-medium text-gray-900 mb-3">用户名检索</h2>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleUsernameSearch(); }}
                  placeholder="输入用户名关键字（模糊匹配，不区分大小写）"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
                <button
                  onClick={handleUsernameSearch}
                  disabled={searchLoading}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-1"
                >
                  <Search size={14} />
                  {searchLoading ? '搜索中...' : '搜索'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                同时检索未删除（绿色标签）和已删除（红色标签）用户，显示其所属机构。
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">用户名</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">角色</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">所属机构</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">备注</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {searchActiveUsers.map(user => (
                    <tr key={`active-${user.id}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">#{user.id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.username}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          正常
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.role}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.school?.schoolname || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={user.memo || ''}>
                        {user.memo || '-'}
                      </td>
                    </tr>
                  ))}
                  {searchDeletedUsersList.map(user => (
                    <tr key={`deleted-${user.id}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">#{user.id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.username}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                          已删除
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.role}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.school?.schoolname ? (
                          <>
                            {user.school.schoolname}
                            {user.school.is_deleted && (
                              <span className="ml-1 text-xs text-red-500">(已删除)</span>
                            )}
                          </>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={user.memo || ''}>
                        {user.memo || '-'}
                      </td>
                    </tr>
                  ))}
                  {hasSearched && !searchLoading && searchActiveUsers.length === 0 && searchDeletedUsersList.length === 0 && (
                    <tr><td colSpan={6} className="text-center py-8 text-gray-400">未找到匹配用户</td></tr>
                  )}
                  {!hasSearched && (
                    <tr><td colSpan={6} className="text-center py-8 text-gray-400">请输入关键字后点击搜索</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* --- REGION TAB (城市区域管理) --- */}
        {activeTab === 'region' && (
          <div className="space-y-6">
            {/* 上级：城市列表 */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900">城市列表 ({regionCities.length})</h2>
                <button onClick={openCreateCity} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">+ 新增城市</button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">城市名</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">创建时间</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {regionCities.map((city) => (
                      <tr key={city.id} className={`hover:bg-gray-50 cursor-pointer ${regionSelectedCityId === city.id ? 'bg-blue-50' : ''}`} onClick={() => setRegionSelectedCityId(city.id)}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">#{city.id}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{city.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{city.created_at ? new Date(city.created_at).toLocaleString('zh-CN') : '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                          <button onClick={() => openEditCity(city)} className="text-indigo-600 hover:text-indigo-900 mr-4">编辑</button>
                          <button onClick={() => handleDeleteCity(city.id)} className="text-red-600 hover:text-red-900">删除</button>
                        </td>
                      </tr>
                    ))}
                    {regionCities.length === 0 && !regionLoading && (
                      <tr><td colSpan={4} className="text-center py-8 text-gray-400">暂无数据</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 下级：区县列表（依赖选中城市） */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900">
                  区县列表 ({regionDistricts.length})
                  {regionSelectedCityId !== null && (
                    <span className="ml-2 text-sm font-normal text-gray-500">
                      — 当前城市：{regionCities.find(c => c.id === regionSelectedCityId)?.name}
                    </span>
                  )}
                </h2>
                <button
                  onClick={openCreateDistrict}
                  disabled={regionSelectedCityId === null}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  + 新增区县
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">所属城市</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">区县名</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">创建时间</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {regionDistricts.map((d) => (
                      <tr key={d.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">#{d.id}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{regionCities.find(c => c.id === d.city_id)?.name || `城市#${d.city_id}`}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{d.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{d.created_at ? new Date(d.created_at).toLocaleString('zh-CN') : '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button onClick={() => openEditDistrict(d)} className="text-indigo-600 hover:text-indigo-900 mr-4">编辑</button>
                          <button onClick={() => handleDeleteDistrict(d.id)} className="text-red-600 hover:text-red-900">删除</button>
                        </td>
                      </tr>
                    ))}
                    {regionDistricts.length === 0 && regionSelectedCityId !== null && (
                      <tr><td colSpan={5} className="text-center py-8 text-gray-400">该城市下暂无区县</td></tr>
                    )}
                    {regionSelectedCityId === null && (
                      <tr><td colSpan={5} className="text-center py-8 text-gray-400">请在上方城市列表中选择一个城市</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* --- School Modal --- */}
      {isSchoolModalOpen && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setIsSchoolModalOpen(false)}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSchoolSubmit} className="p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  {editingSchool ? '编辑学校' : '新增学校'}
                </h3>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">学校名称</label>
                  <input
                    type="text"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500"
                    value={schoolForm.schoolname}
                    onChange={(e) => setSchoolForm({ ...schoolForm, schoolname: e.target.value })}
                  />
                </div>
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button type="submit" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-emerald-600 text-base font-medium text-white hover:bg-emerald-700 sm:col-start-2">
                    保存
                  </button>
                  <button type="button" onClick={() => setIsSchoolModalOpen(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:col-start-1">
                    取消
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* --- Config School Modal (机构配置创建，第 15-16 章) --- */}
      {isConfigSchoolModalOpen && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setIsConfigSchoolModalOpen(false)}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <form onSubmit={handleConfigSchoolSubmit} className="p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">配置创建机构</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">城市 *</label>
                      <select
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500"
                        value={configSchoolForm.city_id}
                        onChange={(e) => handleConfigCityChange(e.target.value)}
                      >
                        <option value="">-- 请选择城市 --</option>
                        {cities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">区县 *</label>
                      <select
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500"
                        value={configSchoolForm.district_id}
                        onChange={(e) => setConfigSchoolForm(prev => ({ ...prev, district_id: e.target.value }))}
                      >
                        <option value="">-- 请选择区县 --</option>
                        {districts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_trial"
                      className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                      checked={configSchoolForm.is_trial}
                      onChange={(e) => setConfigSchoolForm(prev => ({ ...prev, is_trial: e.target.checked }))}
                    />
                    <label htmlFor="is_trial" className="text-sm font-medium text-gray-700">
                      体验课 {configSchoolForm.is_trial && <span className="text-amber-600">(名称含"体验"标识)</span>}
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      上课日 {(configSchoolForm.is_trial || configSchoolForm.day_of_weeks.length > 0) && <span className="text-red-500">*</span>}
                      <span className="text-gray-400 font-normal ml-1">(不选则创建无班次机构)</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {WEEKDAY_LABELS.slice(1).map((label, idx) => {
                        const day = idx + 1;
                        const checked = configSchoolForm.day_of_weeks.includes(day);
                        return (
                          <label key={day} className={`px-3 py-1.5 rounded-md border cursor-pointer text-sm transition-colors ${checked ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                            <input
                              type="checkbox"
                              className="hidden"
                              checked={checked}
                              onChange={() => handleDayOfWeekToggle(day)}
                            />
                            {label}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        开始时间 {(configSchoolForm.is_trial || configSchoolForm.day_of_weeks.length > 0) && <span className="text-red-500">*</span>}
                      </label>
                      <input
                        type="time"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500"
                        value={configSchoolForm.start_time}
                        onChange={(e) => setConfigSchoolForm(prev => ({ ...prev, start_time: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        结束时间 {(configSchoolForm.is_trial || configSchoolForm.day_of_weeks.length > 0) && <span className="text-red-500">*</span>}
                      </label>
                      <input
                        type="time"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500"
                        value={configSchoolForm.end_time}
                        onChange={(e) => setConfigSchoolForm(prev => ({ ...prev, end_time: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        课程类型 {(configSchoolForm.is_trial || configSchoolForm.day_of_weeks.length > 0) && <span className="text-red-500">*</span>}
                      </label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500"
                        value={configSchoolForm.course_type_id}
                        onChange={(e) => setConfigSchoolForm(prev => ({ ...prev, course_type_id: e.target.value }))}
                      >
                        <option value="">-- 请选择 --</option>
                        {courseTypes.map(ct => <option key={ct.id} value={ct.id}>{ct.name} ({ct.code})</option>)}
                      </select>
                    </div>
                  </div>

                  {previewSchoolname() && (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md">
                      <div className="text-xs text-emerald-700 font-medium mb-1">预览机构名称（最终以后端生成为准）</div>
                      <div className="text-sm text-emerald-900 font-mono">{previewSchoolname()}</div>
                    </div>
                  )}
                </div>

                <div className="mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button type="submit" disabled={configSchoolLoading} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-emerald-600 text-base font-medium text-white hover:bg-emerald-700 disabled:bg-gray-300 sm:col-start-2">
                    {configSchoolLoading ? '创建中...' : '创建机构'}
                  </button>
                  <button type="button" onClick={() => setIsConfigSchoolModalOpen(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:col-start-1">
                    取消
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* --- City Modal --- */}
      {isCityModalOpen && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setIsCityModalOpen(false)}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleCitySubmit} className="p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  {editingCity ? '编辑城市' : '新增城市'}
                </h3>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">城市名称</label>
                  <input
                    type="text"
                    required
                    maxLength={50}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    value={cityForm.name}
                    onChange={(e) => setCityForm({ ...cityForm, name: e.target.value })}
                  />
                  <p className="mt-1 text-xs text-gray-400">城市名全局唯一（含已删除记录也会冲突）</p>
                </div>
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button type="submit" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 sm:col-start-2">
                    保存
                  </button>
                  <button type="button" onClick={() => setIsCityModalOpen(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:col-start-1">
                    取消
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* --- District Modal --- */}
      {isDistrictModalOpen && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setIsDistrictModalOpen(false)}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleDistrictSubmit} className="p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  {editingDistrict ? '编辑区县' : '新增区县'}
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">所属城市 *</label>
                    <select
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500"
                      value={districtForm.city_id}
                      onChange={(e) => setDistrictForm({ ...districtForm, city_id: e.target.value })}
                    >
                      <option value="">-- 请选择城市 --</option>
                      {regionCities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">区县名称 *</label>
                    <input
                      type="text"
                      required
                      maxLength={50}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500"
                      value={districtForm.name}
                      onChange={(e) => setDistrictForm({ ...districtForm, name: e.target.value })}
                    />
                    <p className="mt-1 text-xs text-gray-400">同一城市下区县名唯一（含已删除记录也会冲突）</p>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button type="submit" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-emerald-600 text-base font-medium text-white hover:bg-emerald-700 sm:col-start-2">
                    保存
                  </button>
                  <button type="button" onClick={() => setIsDistrictModalOpen(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:col-start-1">
                    取消
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* --- Batch Import Modal (批量导入用户，第 14 章) --- */}
      {isBatchImportModalOpen && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setIsBatchImportModalOpen(false)}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              {batchImportResult ? (
                <div className="p-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">批量导入结果</h3>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="p-3 bg-gray-50 rounded-md text-center">
                      <div className="text-2xl font-bold text-gray-900">{batchImportResult.requested_count}</div>
                      <div className="text-xs text-gray-500">总计</div>
                    </div>
                    <div className="p-3 bg-green-50 rounded-md text-center">
                      <div className="text-2xl font-bold text-green-600">{batchImportResult.success_count}</div>
                      <div className="text-xs text-gray-500">成功</div>
                    </div>
                    <div className="p-3 bg-red-50 rounded-md text-center">
                      <div className="text-2xl font-bold text-red-600">{batchImportResult.failure_count}</div>
                      <div className="text-xs text-gray-500">失败</div>
                    </div>
                  </div>
                  {batchImportResult.success_count > 0 && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">
                      初始密码为 <span className="font-mono font-bold">123456</span>，请通知用户登录后修改。
                    </div>
                  )}
                  {batchImportResult.failed_usernames.length > 0 && (
                    <div className="mb-4">
                      <div className="text-sm font-medium text-gray-700 mb-2">失败列表：</div>
                      <ul className="max-h-40 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100">
                        {batchImportResult.failed_usernames.map((f, i) => (
                          <li key={i} className="px-3 py-2 text-sm flex justify-between">
                            <span className="text-gray-900">{f.username}</span>
                            <span className="text-red-500">{f.reason}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                    <button type="button" onClick={() => setBatchImportResult(null)} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 sm:col-start-2">
                      再次导入
                    </button>
                    <button type="button" onClick={() => setIsBatchImportModalOpen(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:col-start-1">
                      关闭
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleBatchImportSubmit} className="p-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">批量导入用户</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">目标机构 *</label>
                        <select
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                          value={batchImportForm.school_id}
                          onChange={(e) => setBatchImportForm(prev => ({ ...prev, school_id: e.target.value }))}
                        >
                          <option value="">-- 请选择机构 --</option>
                          {schools.filter(s => !s.is_deleted).map(school => (
                            <option key={school.id} value={school.id}>{school.schoolname}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">角色</label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                          value={batchImportForm.role}
                          onChange={(e) => setBatchImportForm(prev => ({ ...prev, role: e.target.value as any }))}
                        >
                          <option value="student">Student</option>
                          <option value="teacher">Teacher</option>
                          <option value="admin">Admin</option>
                          <option value="sys">System</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        用户名列表 *
                        <span className="text-gray-400 font-normal ml-1">(支持逗号、分号、顿号、空格、换行等分隔符)</span>
                      </label>
                      <textarea
                        rows={6}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 font-mono"
                        placeholder="张三，李四；王五、赵六&#10;alice, bob; carol"
                        value={batchImportForm.usernames_raw}
                        onChange={(e) => setBatchImportForm(prev => ({ ...prev, usernames_raw: e.target.value }))}
                      />
                    </div>
                    <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-md text-sm text-indigo-700">
                      所有新创建用户的初始密码为 <span className="font-mono font-bold">123456</span>。重名用户将自动跳过。
                    </div>
                  </div>
                  <div className="mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                    <button type="submit" disabled={batchImportLoading} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 disabled:bg-gray-300 sm:col-start-2">
                      {batchImportLoading ? '导入中...' : '开始导入'}
                    </button>
                    <button type="button" onClick={() => setIsBatchImportModalOpen(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:col-start-1">
                      取消
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* --- User Modal --- */}
      {isUserModalOpen && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setIsUserModalOpen(false)}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleUserSubmit} className="p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  {editingUser ? '编辑用户' : '新增用户'}
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">用户名</label>
                    <input
                      type="text"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      value={userForm.username}
                      onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      密码 {editingUser && <span className="text-gray-400 font-normal">(留空保持不变)</span>}
                    </label>
                    <input
                      type="password"
                      required={!editingUser} 
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      value={userForm.password}
                      onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">角色</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        value={userForm.role}
                        onChange={(e) => setUserForm({ ...userForm, role: e.target.value as any })}
                      >
                        <option value="student">Student</option>
                        <option value="teacher">Teacher</option>
                        <option value="admin">Admin</option>
                        <option value="sys">System</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">所属机构</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        value={userForm.school_id}
                        onChange={(e) => setUserForm({ ...userForm, school_id: e.target.value })}
                      >
                        <option value="">-- 无机构 --</option>
                        {schools.filter(s => !s.is_deleted).map(school => (
                          <option key={school.id} value={school.id}>{school.schoolname}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">备注</label>
                    <textarea
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      value={userForm.memo}
                      onChange={(e) => setUserForm({ ...userForm, memo: e.target.value })}
                      placeholder="请输入用户备注信息..."
                    />
                  </div>
                </div>
                <div className="mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button type="submit" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 sm:col-start-2">
                    保存
                  </button>
                  <button type="button" onClick={() => setIsUserModalOpen(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:col-start-1">
                    取消
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* --- Time Modal (班次新增/编辑) --- */}
      {isTimeModalOpen && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setIsTimeModalOpen(false)}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleTimeSubmit} className="p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  {editingTime ? '编辑班次' : '新增班次'}
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">星期几</label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-amber-500 focus:border-amber-500"
                      value={timeForm.day_of_week}
                      onChange={(e) => setTimeForm({ ...timeForm, day_of_week: Number(e.target.value) })}
                    >
                      {WEEKDAY_LABELS.slice(1).map((label, idx) => (
                        <option key={idx + 1} value={idx + 1}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">开始时间</label>
                      <input
                        type="time"
                        step={1}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-amber-500 focus:border-amber-500"
                        value={timeForm.start_time}
                        onChange={(e) => setTimeForm({ ...timeForm, start_time: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">结束时间</label>
                      <input
                        type="time"
                        step={1}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-amber-500 focus:border-amber-500"
                        value={timeForm.end_time}
                        onChange={(e) => setTimeForm({ ...timeForm, end_time: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button type="submit" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-amber-600 text-base font-medium text-white hover:bg-amber-700 sm:col-start-2">
                    保存
                  </button>
                  <button type="button" onClick={() => setIsTimeModalOpen(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:col-start-1">
                    取消
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* --- Transfer Modal (变更机构，需求 1.1) --- */}
      {isTransferModalOpen && transferTargetUser && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setIsTransferModalOpen(false)}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleTransferSubmit} className="p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">变更用户所属机构</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">用户名</label>
                    <div className="px-3 py-2 bg-gray-50 rounded-md text-sm text-gray-900">{transferTargetUser.username}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">目标机构</label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                      value={transferForm.school_id}
                      onChange={(e) => setTransferForm({ school_id: e.target.value })}
                    >
                      <option value="">-- 无机构 --</option>
                      {schools.filter(s => !s.is_deleted).map(school => (
                        <option key={school.id} value={school.id}>{school.schoolname}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button type="submit" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-cyan-600 text-base font-medium text-white hover:bg-cyan-700 sm:col-start-2">保存</button>
                  <button type="button" onClick={() => setIsTransferModalOpen(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:col-start-1">取消</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* --- Restore User Modal (恢复用户，需求 1.2) --- */}
      {isRestoreUserModalOpen && restoringUser && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setIsRestoreUserModalOpen(false)}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleRestoreUserSubmit} className="p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">恢复已删除用户</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">用户名</label>
                    <div className="px-3 py-2 bg-gray-50 rounded-md text-sm text-gray-900">{restoringUser.username}</div>
                  </div>
                  {restoreUserNeedsNewSchool && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                      原机构已删除，必须指定新的机构才能恢复用户。
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {restoreUserNeedsNewSchool ? '新机构 *' : '新机构（可选）'}
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500"
                      value={restoreUserForm.new_school_id}
                      onChange={(e) => setRestoreUserForm({ new_school_id: e.target.value })}
                    >
                      <option value="">{restoreUserNeedsNewSchool ? '-- 请选择机构 --' : '保留原机构'}</option>
                      {schools.filter(s => !s.is_deleted).map(school => (
                        <option key={school.id} value={school.id}>{school.schoolname}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button type="submit" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 sm:col-start-2">确认恢复</button>
                  <button type="button" onClick={() => setIsRestoreUserModalOpen(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:col-start-1">取消</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Face Enroll Modal */}
      {isFaceEnrollModalOpen && faceEnrollUser && (
        <AdminFaceEnrollModal
          isOpen={isFaceEnrollModalOpen}
          onClose={() => {
            setIsFaceEnrollModalOpen(false);
            setFaceEnrollUser(null);
          }}
          user={faceEnrollUser}
          mode={faceEnrollMode}
          onSuccess={() => {
            setIsFaceEnrollModalOpen(false);
            setFaceEnrollUser(null);
            fetchUsers();
          }}
        />
      )}
    </div>
  );
};

export default UsersPage;
